/**
 * Alternative split-flap display variants for the splash page.
 * Each shows a single word (e.g. "CHARTMIND") using a different animation approach.
 */
import React, { useState, useEffect, useRef } from "react";

const CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ-: ";

function getNextChar(current, target) {
  let ci = CHARS.indexOf(current);
  const ti = CHARS.indexOf(target);
  if (ci === -1) ci = 0;
  if (ti === -1) return target;
  const fwd = (ti - ci + CHARS.length) % CHARS.length;
  const bwd = (ci - ti + CHARS.length) % CHARS.length;
  return fwd <= bwd
    ? CHARS[(ci + 1) % CHARS.length]
    : CHARS[(ci - 1 + CHARS.length) % CHARS.length];
}

const CELL_HEIGHT = 36;
const FLIP_MS = 120;
const STAGGER_MS = 80;

const cellStyle = (backgroundColor) => ({
  width: "2rem",
  height: CELL_HEIGHT,
  margin: "0 2px",
  flexShrink: 0,
  borderRadius: "4px",
  background: backgroundColor || "secondary.main",
  color: "white",
  fontSize: "1.1rem",
  fontFamily: "monospace",
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
});

// --- 1. True split-flap (clip-path): top half of current + bottom half of next flip at hinge ---
export function SplitFlapClipPathVariant({ text = "CHARTMIND", color, animationKey: _key }) {
  const [states, setStates] = useState(() =>
    text.split("").map((c) => ({ currentChar: " ", nextChar: c, targetChar: c, animating: false }))
  );
  const followUpRef = useRef([]);

  useEffect(() => {
    const timeouts = [];
    text.split("").forEach((_, charIndex) => {
      const delay = charIndex * STAGGER_MS;
      timeouts.push(
        setTimeout(() => {
          setStates((prev) => {
            const s = prev[charIndex];
            if (s.currentChar === s.targetChar) return prev;
            const next = getNextChar(s.currentChar, s.targetChar);
            return prev.map((p, i) =>
              i === charIndex ? { ...p, nextChar: next, animating: true } : p
            );
          });
        }, delay)
      );
    });
    return () => timeouts.forEach(clearTimeout);
  }, [text, _key]);

  useEffect(() => {
    const timeouts = [];
    states.forEach((s, charIndex) => {
      if (!s.animating) return;
      const t = setTimeout(() => {
        setStates((prev) => {
          const p = prev[charIndex];
          const nextCur = p.nextChar;
          const nextNext =
            nextCur === p.targetChar ? nextCur : getNextChar(nextCur, p.targetChar);
          const nextStates = prev.map((q, i) =>
            i === charIndex
              ? {
                  ...q,
                  currentChar: nextCur,
                  nextChar: nextNext,
                  animating: false,
                }
              : q
          );
          if (nextCur !== p.targetChar) {
            followUpRef.current.push(
              setTimeout(() => {
                setStates((prev2) =>
                  prev2.map((q, i) =>
                    i === charIndex ? { ...q, animating: true } : q
                  )
                );
              }, FLIP_MS)
            );
          }
          return nextStates;
        });
      }, FLIP_MS);
      timeouts.push(t);
    });
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [states]);

  useEffect(() => {
    return () => {
      followUpRef.current.forEach(clearTimeout);
      followUpRef.current = [];
    };
  }, []);

  return (
    <div className="splash-variant-row" style={{ display: "flex", justifyContent: "center" }}>
      {states.map((s, i) => (
        <div
          key={`${i}-${s.currentChar}-${s.nextChar}-${s.animating}`}
          style={{
            ...cellStyle(color),
            perspective: "400px",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "50%",
              overflow: "hidden",
              clipPath: "polygon(0 0, 100% 0, 100% 50%, 0 50%)",
              transformOrigin: "50% 100%",
              transform: s.animating ? "rotateX(-90deg)" : "rotateX(0deg)",
              transition: `transform ${FLIP_MS}ms ease-in-out`,
              backfaceVisibility: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "200%",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                fontSize: CELL_HEIGHT / 2,
              }}
            >
              <span style={{ lineHeight: 2 }}>{s.currentChar}</span>
            </div>
          </div>
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "50%",
              overflow: "hidden",
              clipPath: "polygon(0 50%, 100% 50%, 100% 100%, 0 100%)",
              transformOrigin: "50% 0%",
              transform: s.animating ? "rotateX(0deg)" : "rotateX(90deg)",
              transition: `transform ${FLIP_MS}ms ease-in-out`,
              backfaceVisibility: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "200%",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                fontSize: CELL_HEIGHT / 2,
              }}
            >
              <span style={{ lineHeight: 2 }}>{s.nextChar}</span>
            </div>
          </div>
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: "50%",
              height: 1,
              background: "rgba(255,255,255,0.4)",
              zIndex: 2,
            }}
          />
        </div>
      ))}
    </div>
  );
}

// --- 2. Slot reel: vertical strip scrolls to show target character ---
function getTargetIndices(word) {
  const upper = String(word).toUpperCase();
  return upper.split("").map((c) => {
    const idx = CHARS.indexOf(c);
    return idx >= 0 ? idx : 0;
  });
}

export function SlotReelVariant({ text = "CHARTMIND", color, animationKey: _key }) {
  const targetIndices = getTargetIndices(text);
  const [indices, setIndices] = useState(() => targetIndices.map(() => 0));

  useEffect(() => {
    const targets = getTargetIndices(text);
    setIndices(targets.map(() => 0));
    const finishMs = (targets.length - 1) * STAGGER_MS + FLIP_MS + 100;
    const t = setTimeout(() => setIndices(targets), finishMs);
    return () => clearTimeout(t);
  }, [text, _key]);

  return (
    <div className="splash-variant-row" style={{ display: "flex", justifyContent: "center" }}>
      {indices.map((charIdx, i) => (
        <div
          key={`${i}-${charIdx}`}
          style={{
            ...cellStyle(color),
            height: CELL_HEIGHT,
            padding: 0,
          }}
        >
          <div
            style={{
              height: CHARS.length * CELL_HEIGHT,
              transform: `translateY(-${charIdx * CELL_HEIGHT}px)`,
              transition: `transform ${FLIP_MS}ms ease-out`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            {CHARS.split("").map((c, j) => (
              <div
                key={j}
                style={{
                  height: CELL_HEIGHT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                {c}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- 3. Flip clock: two half-cards, top rotates down / bottom rotates up ---
export function FlipClockVariant({ text = "CHARTMIND", color, animationKey: _key }) {
  const [states, setStates] = useState(() =>
    text.split("").map((c) => ({ currentChar: " ", nextChar: c, targetChar: c, animating: false }))
  );
  const followUpRef = useRef([]);

  useEffect(() => {
    const timeouts = [];
    text.split("").forEach((_, charIndex) => {
      const delay = charIndex * STAGGER_MS;
      timeouts.push(
        setTimeout(() => {
          setStates((prev) => {
            const s = prev[charIndex];
            if (s.currentChar === s.targetChar) return prev;
            const next = getNextChar(s.currentChar, s.targetChar);
            return prev.map((p, i) =>
              i === charIndex ? { ...p, nextChar: next, animating: true } : p
            );
          });
        }, delay)
      );
    });
    return () => timeouts.forEach(clearTimeout);
  }, [text, _key]);

  useEffect(() => {
    const timeouts = [];
    states.forEach((s, charIndex) => {
      if (!s.animating) return;
      const t = setTimeout(() => {
        setStates((prev) => {
          const p = prev[charIndex];
          const nextCur = p.nextChar;
          const nextNext =
            nextCur === p.targetChar ? nextCur : getNextChar(nextCur, p.targetChar);
          const nextStates = prev.map((q, i) =>
            i === charIndex
              ? { ...q, currentChar: nextCur, nextChar: nextNext, animating: false }
              : q
          );
          if (nextCur !== p.targetChar) {
            followUpRef.current.push(
              setTimeout(() => {
                setStates((prev2) =>
                  prev2.map((q, i) => (i === charIndex ? { ...q, animating: true } : q))
                );
              }, FLIP_MS)
            );
          }
          return nextStates;
        });
      }, FLIP_MS);
      timeouts.push(t);
    });
    return () => timeouts.forEach(clearTimeout);
  }, [states]);

  useEffect(() => {
    return () => {
      followUpRef.current.forEach(clearTimeout);
      followUpRef.current = [];
    };
  }, []);

  return (
    <div className="splash-variant-row" style={{ display: "flex", justifyContent: "center" }}>
      {states.map((s, i) => (
        <div
          key={`${i}-${s.currentChar}-${s.nextChar}-${s.animating}`}
          style={{
            width: "2rem",
            height: CELL_HEIGHT,
            margin: "0 2px",
            flexShrink: 0,
            borderRadius: "4px",
            overflow: "hidden",
            perspective: "400px",
            background: "transparent",
          }}
        >
          <div
            style={{
              height: "50%",
              overflow: "hidden",
              transformOrigin: "50% 100%",
              transform: s.animating ? "rotateX(-90deg)" : "rotateX(0deg)",
              transition: `transform ${FLIP_MS}ms ease-in-out`,
              backfaceVisibility: "hidden",
              ...cellStyle(color),
              alignItems: "flex-end",
              paddingBottom: 2,
              borderRadius: 0,
            }}
          >
            {s.currentChar}
          </div>
          <div
            style={{
              height: "50%",
              overflow: "hidden",
              transformOrigin: "50% 0%",
              transform: s.animating ? "rotateX(0deg)" : "rotateX(90deg)",
              transition: `transform ${FLIP_MS}ms ease-in-out`,
              backfaceVisibility: "hidden",
              ...cellStyle(color),
              alignItems: "flex-start",
              paddingTop: 2,
              borderRadius: 0,
            }}
          >
            {s.nextChar}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- 4. Slide: character slides in from above ---
export function SlideVariant({ text = "CHARTMIND", color, animationKey: _key }) {
  const [states, setStates] = useState(() =>
    text.split("").map((c) => ({ displayChar: " ", targetChar: c, slideFromTop: false }))
  );
  const slideTimeoutsRef = useRef([]);

  useEffect(() => {
    const targetChars = text.split("");
    const timeouts = [];
    targetChars.forEach((targetChar, charIndex) => {
      const delay = charIndex * STAGGER_MS;
      timeouts.push(
        setTimeout(() => {
          setStates((prev) => {
            const s = prev[charIndex];
            if (s.displayChar === targetChar) return prev;
            const next = getNextChar(s.displayChar, targetChar);
            return prev.map((p, i) =>
              i === charIndex ? { ...p, displayChar: next, slideFromTop: true } : p
            );
          });
        }, delay)
      );
    });
    return () => timeouts.forEach(clearTimeout);
  }, [text, _key]);

  useEffect(() => {
    const rafs = [];
    states.forEach((s, charIndex) => {
      if (!s.slideFromTop) return;
      rafs.push(
        requestAnimationFrame(() => {
          setStates((prev) =>
            prev.map((p, i) => (i === charIndex ? { ...p, slideFromTop: false } : p))
          );
          const t = setTimeout(() => {
            setStates((prev) => {
              const p = prev[charIndex];
              if (p.displayChar === p.targetChar) return prev;
              const next = getNextChar(p.displayChar, p.targetChar);
              return prev.map((q, i) =>
                i === charIndex ? { ...q, displayChar: next, slideFromTop: true } : q
              );
            });
          }, FLIP_MS);
          slideTimeoutsRef.current.push(t);
        })
      );
    });
    return () => {
      rafs.forEach((r) => cancelAnimationFrame(r));
      slideTimeoutsRef.current.forEach(clearTimeout);
      slideTimeoutsRef.current = [];
    };
  }, [states]);

  return (
    <div className="splash-variant-row" style={{ display: "flex", justifyContent: "center" }}>
      {states.map((s, i) => (
        <div
          key={`${i}-${s.displayChar}-${s.slideFromTop}`}
          style={{
            ...cellStyle(color),
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              transform: s.slideFromTop ? "translateY(-100%)" : "translateY(0)",
              transition: `transform ${FLIP_MS}ms ease-out`,
            }}
          >
            {s.displayChar}
          </div>
        </div>
      ))}
    </div>
  );
}
